// App do jogador (docs/05 §4): lobby → pergunta → enviada → revelação →
// placar → pódio. Reconexão com faixa amarela; resposta enfileirada offline.
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useRoom } from "@/shared/game";
import { fmt, s } from "@/shared/strings";
import { Logo } from "@/shared/components/Logo";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import type { QuestionView } from "@/shared/protocol";
import {
  TeamBadge,
  TeamDot,
  TeamStandings,
  teamColor,
  teamName,
} from "@/shared/components/TeamView";
import { Check, Flame, Pencil, Trophy, Wifi, WifiOff, X } from "lucide-react";

const REACTIONS = ["👏", "🎉", "😂", "😮", "❤️", "🔥"];
const OPTION_COLORS = ["bg-p1", "bg-p2", "bg-p3", "bg-p4", "bg-p5", "bg-p6", "bg-p7", "bg-p8"];
const OPTION_LETTERS = ["A", "B", "C", "D", "E", "F", "G", "H"];

export function Play() {
  const { code = "" } = useParams();
  const nav = useNavigate();
  const token = sessionStorage.getItem(`arena.player.${code}`) ?? undefined;
  const nick = sessionStorage.getItem(`arena.nick.${code}`) ?? "";
  const room = useRoom("player", code, token, !!code);

  useEffect(() => {
    if (!token) nav(`/j/${code}`, { replace: true });
  }, [token, code, nav]);

  return (
    <main className="bg-bg flex min-h-dvh flex-col">
      <TopBar code={code} nick={nick} status={room.status} />
      {room.status === "reconnecting" && (
        <div className="bg-warn text-14 px-4 py-1.5 text-center font-bold text-black">
          {s.reconnecting}
        </div>
      )}
      {room.kicked ? (
        <Center>
          <p className="text-28 text-bad font-black">{s.kicked}</p>
          <Button variant="secondary" onClick={() => nav("/")}>
            OK
          </Button>
        </Center>
      ) : (
        <PhaseView room={room} nick={nick} />
      )}
    </main>
  );
}

function TopBar({ code, nick, status }: { code: string; nick: string; status: string }) {
  return (
    <header className="border-line flex items-center justify-between border-b px-4 py-2">
      <span className="expanded text-16 text-amber font-black tracking-widest">
        {code.toUpperCase()}
      </span>
      <div className="flex items-center gap-2">
        <span className="text-14 text-chalk-2 max-w-32 truncate font-semibold">{nick}</span>
        {status === "open" ? (
          <Wifi className="text-ok h-4 w-4" aria-label="conectado" />
        ) : (
          <WifiOff className="text-warn h-4 w-4" aria-label="reconectando" />
        )}
      </div>
    </header>
  );
}

function PhaseView({ room, nick }: { room: ReturnType<typeof useRoom>; nick: string }) {
  switch (room.phase) {
    case "lobby":
      return <Lobby room={room} nick={nick} />;
    case "intro":
      return (
        <Center>
          <p className="text-22 text-chalk-2">
            {s.questionOf
              ? fmt(s.questionOf, { n: room.questionIndex + 1, total: room.questionCount })
              : ""}
          </p>
          <p className="text-40 text-amber animate-pulse-soft font-black">…</p>
        </Center>
      );
    case "answering":
      return <Answering room={room} />;
    case "judging":
    case "review":
      return (
        <Center>
          <p className="text-22 text-chalk-2 animate-pulse-soft">{s.judging}</p>
        </Center>
      );
    case "reveal":
      return <Reveal room={room} />;
    case "leaderboard":
      return <Leaderboard room={room} />;
    case "podium":
    case "ended":
      return <Podium room={room} />;
    default:
      return (
        <Center>
          <Logo size="sm" />
          <p className="text-18 text-chalk-2 animate-pulse-soft">{s.waitingHost}</p>
        </Center>
      );
  }
}

// ---- lobby ----

function Lobby({ room, nick }: { room: ReturnType<typeof useRoom>; nick: string }) {
  const players = room.players ?? [];
  const nTeams = room.settings?.teams ?? 0;
  const myTeam = room.you?.team ?? 0;
  const [reacted, setReacted] = useState<string | null>(null);
  return (
    <div className="flex flex-1 flex-col items-center gap-6 px-6 py-8">
      <h1 className="text-28 text-ok font-black">{s.youreIn}</h1>
      <div className="flex items-center gap-2">
        <p className="text-18 text-chalk-2 font-semibold">{nick}</p>
        <TeamBadge team={myTeam} className="text-14" />
      </div>
      <p className="text-16 text-chalk-3 animate-pulse-soft">{s.waitingHost}</p>

      {nTeams > 0 && (
        <div className="flex flex-col items-center gap-2">
          <p className="text-16 text-chalk-3 font-bold">{s.pickTeam}</p>
          <div className="flex flex-wrap justify-center gap-2">
            {Array.from({ length: nTeams }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => room.send("choose_team", { team: n })}
                className={`text-16 rounded-full px-3 py-1.5 font-bold text-white transition-transform ${myTeam === n ? "ring-chalk scale-105 ring-4" : "opacity-70"}`}
                style={{ background: teamColor(n) }}
              >
                {teamName(n)}
              </button>
            ))}
          </div>
        </div>
      )}

      <ReactionRow room={room} reacted={reacted} setReacted={setReacted} />

      <div className="mt-2 flex flex-wrap justify-center gap-2">
        {players.map((p) => (
          <Badge key={p.id} variant="secondary" className="text-14 flex items-center gap-1.5">
            <TeamDot team={p.team ?? 0} />
            {p.nickname}
          </Badge>
        ))}
      </div>
      <p className="text-14 text-chalk-3">{fmt(s.playersCount, { n: players.length })}</p>
    </div>
  );
}

// ReactionRow — reações do lobby/placar (agregadas no palco — docs/06 M5).
function ReactionRow({
  room,
  reacted,
  setReacted,
}: {
  room: ReturnType<typeof useRoom>;
  reacted: string | null;
  setReacted: (e: string | null) => void;
}) {
  return (
    <div className="flex justify-center gap-3">
      {REACTIONS.map((e) => (
        <button
          key={e}
          className={`text-28 rounded-full p-2 transition-transform ${reacted === e ? "scale-125" : "active:scale-110"}`}
          onClick={() => {
            room.send("react", { emoji: e });
            setReacted(e);
            setTimeout(() => setReacted(null), 1500);
          }}
          aria-label={`reagir ${e}`}
        >
          {e}
        </button>
      ))}
    </div>
  );
}

// ---- pergunta / resposta ----

function Answering({ room }: { room: ReturnType<typeof useRoom> }) {
  const q = room.question;
  const answered = !!room.you?.answer || !!room.answerAck?.accepted;
  const [editing, setEditing] = useState(false);

  if (!q) return null;
  return (
    <div className="flex flex-1 flex-col">
      <TimerBar room={room} />
      <div className="flex-1 overflow-y-auto px-5 py-4">
        <p className="text-14 text-chalk-3 mb-1">
          {fmt(s.questionOf, { n: room.questionIndex + 1, total: room.questionCount })}
        </p>
        <h1 className="text-22 leading-snug font-bold">{q.prompt}</h1>
        {q.imageUrl && (
          <img src={q.imageUrl} alt="" className="mt-3 max-h-40 rounded-lg object-contain" />
        )}
      </div>
      <div className="border-line border-t px-5 py-4">
        {answered && !editing ? (
          <SentCard room={room} onEdit={() => setEditing(true)} />
        ) : (
          <AnswerInput
            q={q}
            onSubmit={(v) => {
              room.send("answer", { questionIndex: room.questionIndex, value: v });
              setEditing(false);
            }}
          />
        )}
      </div>
    </div>
  );
}

function TimerBar({ room }: { room: ReturnType<typeof useRoom> }) {
  const [left, setLeft] = useState(0);
  const total = (room.question?.timeLimitS ?? 20) * 1000;
  useEffect(() => {
    if (!room.deadline) return;
    const t = setInterval(() => {
      setLeft(Math.max(0, room.deadline! - room.serverNow()));
    }, 100);
    return () => clearInterval(t);
  }, [room.deadline]); // eslint-disable-line react-hooks/exhaustive-deps
  const pct = total > 0 ? (left / total) * 100 : 0;
  const urgent = left < 5000 && left > 0;
  return (
    <div className="bg-bg-3 relative h-3 w-full">
      <div
        className={`h-full transition-[width] duration-100 ${urgent ? "bg-bad" : "bg-amber"}`}
        style={{ width: `${pct}%` }}
      />
      <span className="text-14 absolute top-4 right-2 font-bold tabular-nums">
        {Math.ceil(left / 1000)}s
      </span>
    </div>
  );
}

function SentCard({ room, onEdit }: { room: ReturnType<typeof useRoom>; onEdit: () => void }) {
  const allowEdit = room.answerAck?.editable;
  return (
    <div className="bg-amber-soft flex flex-col items-center gap-3 rounded-xl p-5">
      <p className="text-18 text-amber flex items-center gap-2 font-bold">
        <Check className="h-5 w-5" /> {s.answerSent}
      </p>
      {room.you?.answer?.raw && (
        <p className="text-20 text-chalk max-w-full truncate font-semibold">
          {room.you.answer.raw}
        </p>
      )}
      {allowEdit && (
        <>
          <p className="text-13 text-ink/70">{s.canEditUntil}</p>
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Pencil className="h-4 w-4" /> {s.edit}
          </Button>
        </>
      )}
    </div>
  );
}

function AnswerInput({ q, onSubmit }: { q: QuestionView; onSubmit: (v: string) => void }) {
  switch (q.mode) {
    case "multiple_choice":
      return <MCInput q={q} onSubmit={onSubmit} />;
    case "true_false":
      return <TFInput onSubmit={onSubmit} />;
    case "number":
      return <NumberInput onSubmit={onSubmit} unit={q.unit} />;
    case "poll":
      return <PollInput q={q} onSubmit={onSubmit} />;
    default:
      return <TextInput onSubmit={onSubmit} />;
  }
}

function MCInput({ q, onSubmit }: { q: QuestionView; onSubmit: (v: string) => void }) {
  const [sel, setSel] = useState<number[]>([]);
  const multi = !!q.multiSelect;
  const toggle = (i: number) => {
    if (multi) {
      setSel((cur) => (cur.includes(i) ? cur.filter((x) => x !== i) : [...cur, i]));
    } else {
      onSubmit(String(i));
    }
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3">
        {(q.options ?? []).map((o, i) => (
          <button
            key={i}
            onClick={() => toggle(i)}
            className={`${OPTION_COLORS[i % 8]} ${sel.includes(i) ? "ring-chalk ring-4" : ""} text-22 flex min-h-24 items-center justify-center gap-2 rounded-xl p-3 font-black text-white active:scale-95`}
          >
            <span className="text-28">{OPTION_LETTERS[i]}</span>
            <span className="text-16 leading-tight">{o.text}</span>
          </button>
        ))}
      </div>
      {multi && (
        <Button size="lg" disabled={sel.length === 0} onClick={() => onSubmit(sel.join(","))}>
          {s.send}
        </Button>
      )}
    </div>
  );
}

function TFInput({ onSubmit }: { onSubmit: (v: string) => void }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      <button
        onClick={() => onSubmit("0")}
        className="bg-ok text-28 flex min-h-28 items-center justify-center gap-2 rounded-xl font-black text-white active:scale-95"
      >
        <Check className="h-8 w-8" /> {s.trueLabel}
      </button>
      <button
        onClick={() => onSubmit("1")}
        className="bg-bad text-28 flex min-h-28 items-center justify-center gap-2 rounded-xl font-black text-white active:scale-95"
      >
        <X className="h-8 w-8" /> {s.falseLabel}
      </button>
    </div>
  );
}

function NumberInput({ onSubmit, unit }: { onSubmit: (v: string) => void; unit?: string }) {
  const [v, setV] = useState("");
  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", ",", "0", "⌫"];
  return (
    <div className="flex flex-col gap-3">
      <div className="bg-bg-2 border-line flex items-baseline justify-center gap-2 rounded-xl border p-4">
        <span className="text-40 font-black tabular-nums">{v || "0"}</span>
        {unit && <span className="text-18 text-chalk-3">{unit}</span>}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {keys.map((k) => (
          <button
            key={k}
            className="bg-bg-2 border-line text-28 rounded-lg border py-3 font-bold active:scale-95"
            onClick={() => setV((cur) => (k === "⌫" ? cur.slice(0, -1) : cur + k))}
          >
            {k}
          </button>
        ))}
      </div>
      <Button size="lg" disabled={!v} onClick={() => onSubmit(v)}>
        {s.send}
      </Button>
    </div>
  );
}

function PollInput({ q, onSubmit }: { q: QuestionView; onSubmit: (v: string) => void }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-16 text-chalk-2 text-center">{s.votePoll}</p>
      {(q.options ?? []).map((o, i) => (
        <button
          key={i}
          onClick={() => onSubmit(String(i))}
          className={`${OPTION_COLORS[i % 8]} text-22 flex min-h-16 items-center justify-center rounded-xl p-3 font-bold text-white active:scale-95`}
        >
          {o.text}
        </button>
      ))}
    </div>
  );
}

function TextInput({ onSubmit }: { onSubmit: (v: string) => void }) {
  const [v, setV] = useState("");
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (v.trim()) onSubmit(v.trim().slice(0, 200));
      }}
    >
      <Input
        value={v}
        onChange={(e) => setV(e.target.value.slice(0, 200))}
        placeholder={s.typeAnswer}
        autoCapitalize="off"
        autoComplete="off"
        enterKeyHint="send"
        className="text-22 h-14"
        maxLength={200}
      />
      <Button type="submit" size="lg" disabled={!v.trim()}>
        {s.send}
      </Button>
    </form>
  );
}

// ---- revelação ----

function Reveal({ room }: { room: ReturnType<typeof useRoom> }) {
  const you = room.reveal?.you;
  const verdict = you?.verdict ?? room.you?.verdict;
  useEffect(() => {
    if (verdict === "correct" && navigator.vibrate) navigator.vibrate(30);
  }, [verdict]);

  const styles: Record<string, { bg: string; label: string }> = {
    correct: { bg: "bg-ok", label: s.correct },
    partial: { bg: "bg-warn", label: s.partial },
    wrong: { bg: "bg-bad", label: s.wrong },
    review: { bg: "bg-amber", label: s.review },
    manipulation: { bg: "bg-amber", label: s.manipulation },
  };
  const st = styles[verdict ?? "wrong"] ?? styles.wrong;
  const key = room.reveal?.answerKey;
  return (
    <div className={`flex flex-1 flex-col items-center justify-center gap-4 px-6 ${st.bg}`}>
      <p className="text-56 font-black text-white">{st.label}</p>
      {verdict === "manipulation" && (
        <p className="text-22 max-w-xs text-center font-semibold text-white/90">
          {s.manipulationSub}
        </p>
      )}
      {you && you.points > 0 && (
        <p className="text-56 font-black text-white tabular-nums">
          {fmt(s.pointsEarned, { points: you.points })}
        </p>
      )}
      {you?.rank != null && (
        <p className="text-22 font-bold text-white/90">
          {fmt(s.youAreRank, { rank: you.rank })}
          {you.delta !== 0 && (
            <span className="ml-1">{you.delta < 0 ? `(↑${-you.delta})` : `(↓${you.delta})`}</span>
          )}
        </p>
      )}
      {key && <AnswerKeyView k={key} />}
      {room.question?.hint && (
        <p className="text-16 max-w-sm rounded-lg bg-black/20 px-4 py-2 text-center font-semibold text-white/90">
          {s.hintLabel}: {room.question.hint}
        </p>
      )}
      <p className="text-16 text-white/70">{s.waitNext}</p>
    </div>
  );
}

function AnswerKeyView({
  k,
}: {
  k: NonNullable<ReturnType<typeof useRoom>["reveal"]>["answerKey"];
}) {
  if (!k) return null;
  let text = "";
  if (k.correctOptions?.length) text = k.correctOptions.map((i) => OPTION_LETTERS[i]).join(", ");
  else if (typeof k.answer === "boolean") text = k.answer ? s.trueLabel : s.falseLabel;
  else if (k.answer != null) text = String(k.answer);
  if (!text) return null;
  return (
    <p className="text-18 rounded-lg bg-black/20 px-4 py-2 font-semibold text-white">
      {s.rightAnswer}: {text}
    </p>
  );
}

// ---- placar / pódio ----

function Leaderboard({ room }: { room: ReturnType<typeof useRoom> }) {
  const lb = room.leaderboard;
  const you = lb?.you;
  const top = lb?.top ?? [];
  const [reacted, setReacted] = useState<string | null>(null);
  return (
    <div className="flex flex-1 flex-col gap-4 px-5 py-6">
      {you && (
        <div className="bg-amber-soft border-amber rounded-xl border-2 p-4">
          <p className="text-22 text-amber font-black">
            {fmt(s.youAreRank, { rank: you.rank })} · {fmt(s.finalScore, { score: you.score })}
          </p>
        </div>
      )}
      <div className="flex flex-col gap-2">
        {top.slice(0, 5).map((e) => (
          <div
            key={e.playerId}
            className="bg-bg-2 flex items-center justify-between rounded-lg px-4 py-3"
          >
            <span className="text-18 flex items-center gap-2 font-bold">
              <TeamDot team={e.team ?? 0} />
              {e.rank}º {e.nickname}
            </span>
            <span className="text-18 font-bold tabular-nums">{e.score}</span>
          </div>
        ))}
      </div>
      {lb?.teams && lb.teams.length > 0 && <TeamStandings teams={lb.teams} />}
      <ReactionRow room={room} reacted={reacted} setReacted={setReacted} />
      <p className="text-16 text-chalk-3 animate-pulse-soft text-center">{s.waitNext}</p>
    </div>
  );
}

function Podium({ room }: { room: ReturnType<typeof useRoom> }) {
  const you = room.podium?.you;
  const onPodium = you && you.rank <= 3;
  const teams = room.podium?.teams;
  const champion = teams?.length ? [...teams].sort((a, b) => b.score - a.score)[0] : undefined;
  return (
    <div
      className={`flex flex-1 flex-col items-center justify-center gap-4 px-6 ${onPodium ? "bg-amber" : ""}`}
    >
      <Trophy className={`h-16 w-16 ${onPodium ? "text-amber-ink" : "text-amber"}`} />
      {champion && (
        <p
          className="text-18 rounded-full px-4 py-1.5 font-black text-white"
          style={{ background: teamColor(champion.team) }}
        >
          {s.teamChampion}: {teamName(champion.team)}
        </p>
      )}
      {you ? (
        <p
          className={`text-28 text-center font-black ${onPodium ? "text-amber-ink" : "text-chalk"}`}
        >
          {fmt(s.podiumYou, { rank: you.rank, score: you.score })}
        </p>
      ) : (
        <p className="text-22 text-chalk-2">{s.sessionEnded}</p>
      )}
      {(room.you?.streak ?? 0) >= 3 && (
        <p className="text-18 flex items-center gap-1 font-bold">
          <Flame className="text-bad h-5 w-5" /> {room.you?.streak} 🔥
        </p>
      )}
    </div>
  );
}

function Center({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-5 px-6">{children}</div>
  );
}
